/**
 *
 * Form
 *
 */

import React from "react";
import { FormattedMessage } from "react-intl";
import scrollToComponent from "react-scroll-to-component";
import PropTypes from "prop-types";
import forEach from "lodash/forEach";
import remove from "lodash/remove";
import filter from "lodash/filter";
import { keyCodes } from "root/libs/constants/src";
import { validations, IntlPropType } from "root/libs/core-libs/src";
import { DhlText } from "@dhl-official/react-library";
import { Sizes } from "@dhl-official/stencil-library";
import ResponsiveRender from "one-time-shipment/src/components/Layout/ResponsiveRender";
import { groupBy } from "lodash";
import messages from "./messages";
import {
  StyledButtonWrapper,
  StyledButtonHintText,
  StyledButtonWithPendingState,
  StyledMainWrapper,
  StepNoteCell,
} from "./styled";

const COPY_ID_PREFIX_NAVIGATION = "NavigationButton";

const COPY_ID_PREFIX_STEPPER = "Stepper";

export default class Form extends React.PureComponent {
  static propTypes = {
    children: PropTypes.any.isRequired,
    onSubmit: PropTypes.func.isRequired,
    /** Use when you need your own Submit button
     * to trigger form.submit from your componet.
     * In there you would simply add to the onClick of your button
     * form.submit(); */
    formReference: PropTypes.object,
    validateDomElement: PropTypes.func.isRequired,
    className: PropTypes.string,
    isButtonDisabled: PropTypes.bool,
    isButtonPending: PropTypes.bool,
    buttonLabel: PropTypes.string.isRequired,
    buttonTestId: PropTypes.string,
    buttonAriaDescribedBy: PropTypes.string,
    showButtonHint: PropTypes.bool,
    stepperEnabled: PropTypes.bool,
    step: PropTypes.number,
    buttonHintLabel: PropTypes.string,
    autoScrollToFirstValidationError: PropTypes.bool,
    isFormValid: PropTypes.oneOf([true, false, null]),
    /** object key path to an interactive element to the digitalLayer */
    buttonDataTrackingKey: PropTypes.string,
    /** Action to be dispached to update the tracking key of the button (if present) in the digitalLayer */
    trackEvent: PropTypes.func,
    /** The trackEventKey (one string without dots) that will be updated in the digitalLayer
     * by the trackEvent acti on.
     * The submit value will be
     * - in case of a successful submit: the current timestamp
     * - in case of a validation error: VALIDATION_ERRORS_SHOWN
     */
    trackEventKey: PropTypes.string,
    /** If true you are adding your own submit button in your own component */
    useMyOwnSubmitButton: PropTypes.bool,
    buttonAlignment: PropTypes.shape({
      horizontal: PropTypes.oneOf(["center", "left", "right"]),
      addGridContainer: PropTypes.bool,
    }),
    /** Additional key to look up and display specific message variations */
    copyNamespace: PropTypes.string.isRequired,
    dataTestid: PropTypes.string,
    wasFormSubmittedOnce: PropTypes.bool,
    intl: IntlPropType,
  };

  static defaultProps = {
    buttonTestId: undefined,
    className: "",
    showButtonHint: false,
    stepperEnabled: false,
    step: 0,
    formReference: React.createRef(),
    autoScrollToFirstValidationError: false,
    buttonHintLabel: "",
    buttonAriaDescribedBy: "",
    isFormValid: null,
    isButtonDisabled: false,
    isButtonPending: false,
    buttonDataTrackingKey: "",
    trackEvent: () => {},
    trackEventKey: "",
    buttonAlignment: {
      horizontal: "center",
      addGridContainer: false,
    },
    useMyOwnSubmitButton: false,
    dataTestid: undefined,
    wasFormSubmittedOnce: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      amountErrorFields: "0",
      wasFormSubmittedOnce: props.wasFormSubmittedOnce, // eslint-disable-line react/destructuring-assignment
    };
  }

  onSubmit = (event) => {
    event.preventDefault();
    const { form } = event.currentTarget;
    const {
      onSubmit,
      trackEvent,
      trackEventKey,
      useMyOwnSubmitButton,
    } = this.props;

    let formFields = form;
    let submitDelay = 10;
    if (useMyOwnSubmitButton && !formFields) {
      formFields = {
        elements: event.currentTarget,
      };
    }

    // If a Input field is focussed and the form is sent via
    // Enter key, the input is blured before to trigger validation
    if (document.activeElement.tagName === "INPUT") {
      submitDelay = 400;
      document.activeElement.blur();
    }

    this.setState({
      wasFormSubmittedOnce: true,
    });

    this.validateAll(formFields);

    setTimeout(() => {
      const { autoScrollToFirstValidationError } = this.props;
      if (this.isFormValid(formFields)) {
        onSubmit();
        trackEvent({ [trackEventKey]: Date.now() });
      } else {
        trackEvent({ [trackEventKey]: "VALIDATION_ERRORS_SHOWN" });
        const firstFieldWithError = this.getFirstNodeWithError(formFields);

        if (autoScrollToFirstValidationError) {
          this.scrollToFirstNode(firstFieldWithError);
        }
        // Focus first field with error
        if (firstFieldWithError) {
          firstFieldWithError.focus();
        }
      }
    }, submitDelay);
  };

  isFormValid = (form) => {
    const { isFormValid } = this.props;
    return isFormValid !== null ? isFormValid : form.checkValidity();
  };

  isGroupCheckbox = (elem) =>
    elem.type === "checkbox" && elem.required === true;

  isGroupRadio = (elem) => elem.type === "radio" && elem.required === true;

  getFirstNodeWithError = (form) => {
    const allElements = form.elements;
    const fieldsWithError = filter(allElements, (elem) => {
      if (!elem || !elem.parentNode) {
        return false;
      }
      if (!document.contains(elem)) {
        return false;
      }
      return (
        elem.tagName !== "BUTTON" &&
        typeof elem.getAttribute === "function" &&
        elem.getAttribute("aria-invalid") === "true"
      );
    });

    this.setState({ amountErrorFields: fieldsWithError.length });
    return fieldsWithError[0];
  };

  getCopy = (id, values, prefix = COPY_ID_PREFIX_NAVIGATION) => {
    const { intl } = this.props;
    return intl.formatMessage({ id: `${prefix}.${id}` }, values);
  };

  scrollToFirstNode = (firstFieldWithError) => {
    scrollToComponent(firstFieldWithError, {
      offset: -180,
      align: "top",
    });
  };

  submitFormOnReturn = () => {
    const { formReference } = this.props;

    const currentForm = formReference.current;
    const that = this;

    if (
      currentForm !== null &&
      currentForm.querySelectorAll("button").length === 0
    ) {
      currentForm.onkeydown = (e) => {
        const focusedElement = document.activeElement;
        if (
          e.keyCode === keyCodes.RETURN &&
          focusedElement.tagName === "INPUT"
        ) {
          // Exception for auto complete field. When the suggestion list is open
          // and one entry is focused, the form will not be submitted.
          if (
            focusedElement.getAttribute("aria-activedescendant") !== null &&
            focusedElement.getAttribute("aria-activedescendant").length > 0
          ) {
            return;
          }
          that.onSubmit(e);
        }
      };
    }
  };

  validateGroupOfRequiredCheckboxes = (allElements) => {
    const { validateDomElement } = this.props;

    const requiredCheckboxes = filter(allElements, this.isGroupCheckbox);
    const checkboxesToValidate = [];

    forEach(requiredCheckboxes, (element) => {
      const validationArgs = validations.extractValidationProperties(element);
      checkboxesToValidate.push(validationArgs);
    });

    if (checkboxesToValidate.length > 0) {
      validateDomElement(checkboxesToValidate);
    }
  };

  validateGroupOfRequiredRadios = (allElements) => {
    const { validateDomElement } = this.props;
    const requiredRadios = filter(allElements, this.isGroupRadio);
    const radios = [];

    forEach(requiredRadios, (element) => {
      const validationArgs = validations.extractValidationProperties(element);
      radios.push(validationArgs);
    });

    const groupedRadios = groupBy(radios, "name");
    forEach(groupedRadios, (radioGroup) => {
      const radio =
        radioGroup.find((radioElement) => radioElement.checked) ??
        radioGroup[0];
      validateDomElement(radio);
    });
  };

  validateAll = (form) => {
    const allElements = form.elements;
    const elements = filter(allElements, (elem) => elem.tagName !== "BUTTON");

    const { validateDomElement } = this.props;

    remove(elements, this.isGroupCheckbox);
    remove(elements, this.isGroupRadio);
    this.validateGroupOfRequiredCheckboxes(allElements);
    this.validateGroupOfRequiredRadios(allElements);

    forEach(elements, (element) => {
      const validationArgs = validations.extractValidationProperties(element);
      validateDomElement(validationArgs);
    });
  };

  render() {
    const {
      children,
      className,
      isButtonDisabled,
      isButtonPending,
      buttonLabel,
      buttonTestId,
      buttonHintLabel,
      buttonAriaDescribedBy,
      showButtonHint,
      stepperEnabled,
      step,
      buttonDataTrackingKey,
      buttonAlignment,
      useMyOwnSubmitButton,
      formReference,
      isFormValid,
      copyNamespace,
      dataTestid,
    } = this.props;

    const { wasFormSubmittedOnce, amountErrorFields } = this.state;

    const buttonClasses = ["button-wrapper"];

    if (buttonAlignment.addGridContainer) {
      buttonClasses.push("grid-maxWidth");
    }

    let formExtraProps = {};
    if (useMyOwnSubmitButton) {
      formExtraProps = {
        onSubmit: this.onSubmit,
      };

      this.submitFormOnReturn();
    }

    return (
      <form
        ref={formReference}
        noValidate
        autoComplete="off"
        className={stepperEnabled ? "" : className}
        {...formExtraProps}
        data-testid={dataTestid}
      >
        {stepperEnabled ? (
          <React.Fragment>
            <StyledMainWrapper
              className={className}
              step={step}
              stepperEnabled={stepperEnabled}
            >
              {children}
            </StyledMainWrapper>
            {!useMyOwnSubmitButton && (
              <StyledButtonWrapper
                className={buttonClasses.join(" ")}
                buttonAlignment={buttonAlignment.horizontal}
              >
                {showButtonHint && (
                  <StyledButtonHintText center maxWidth="300px" size={Sizes.SM}>
                    {buttonHintLabel}
                  </StyledButtonHintText>
                )}
                <StyledButtonWithPendingState
                  type="submit"
                  value="button"
                  stepperEnabled={stepperEnabled}
                  clickEvent={this.onSubmit}
                  {...((isButtonDisabled || isButtonPending) && {
                    isDisabled: true,
                  })}
                  dataTestid={buttonTestId}
                  dataTracking={buttonDataTrackingKey}
                  dataAriaLabelledby={buttonAriaDescribedBy}
                  className={isButtonPending ? "pending" : undefined}
                >
                  {buttonLabel}
                </StyledButtonWithPendingState>
                <ResponsiveRender
                  render={(isMobile) =>
                    isMobile &&
                    stepperEnabled && (
                      <StepNoteCell>
                        {this.getCopy(
                          "next",
                          undefined,
                          COPY_ID_PREFIX_NAVIGATION
                        )}{" "}
                        :{" "}
                        {this.getCopy(
                          "shipmentDetails",
                          undefined,
                          COPY_ID_PREFIX_STEPPER
                        )}
                      </StepNoteCell>
                    )
                  }
                />
              </StyledButtonWrapper>
            )}

            {!isFormValid && wasFormSubmittedOnce && (
              <DhlText
                dataAriaLive="assertive"
                dataRole="status"
                className="visually-hidden"
              >
                <FormattedMessage
                  id={
                    messages[
                      `${copyNamespace}_screenReaderLabel_formErrorMessage`
                    ].id
                  }
                  values={{ numberOfErrors: amountErrorFields }}
                />
              </DhlText>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {children}
            {!useMyOwnSubmitButton && (
              <StyledButtonWrapper
                className={buttonClasses.join(" ")}
                buttonAlignment={buttonAlignment.horizontal}
              >
                {showButtonHint && (
                  <StyledButtonHintText center maxWidth="300px" size={Sizes.SM}>
                    {buttonHintLabel}
                  </StyledButtonHintText>
                )}
                <StyledButtonWithPendingState
                  type="submit"
                  value="button"
                  clickEvent={this.onSubmit}
                  {...((isButtonDisabled || isButtonPending) && {
                    isDisabled: true,
                  })}
                  dataTestid={buttonTestId}
                  dataTracking={buttonDataTrackingKey}
                  dataAriaLabelledby={buttonAriaDescribedBy}
                  className={isButtonPending ? "pending" : undefined}
                >
                  {buttonLabel}
                </StyledButtonWithPendingState>
              </StyledButtonWrapper>
            )}

            {!isFormValid && wasFormSubmittedOnce && (
              <DhlText
                dataAriaLive="assertive"
                dataRole="status"
                className="visually-hidden"
              >
                <FormattedMessage
                  id={
                    messages[
                      `${copyNamespace}_screenReaderLabel_formErrorMessage`
                    ].id
                  }
                  values={{ numberOfErrors: amountErrorFields }}
                />
              </DhlText>
            )}
          </React.Fragment>
        )}
      </form>
    );
  }
}
